import { Api, type EdgePerson, type FaceEdgeRecognize, type IdentityPersonItem } from './Api';

export interface PersonFieldNames {
    vendor: string;
    email: string;
    phone: string;
    dob: string;
}

export interface PersonCard {
    personId: string;
    faceId: string | null;
    fullName: string;
    vendor: string | null;
    email: string | null;
    phone: string | null;
    dob: string | null;
    tags: string[];
    enrollmentPhotoUrl: string | null;
}

/** Normalizes a raw enrollment-photo string into something usable as an <img src>. */
export function toPhotoUrl(raw: string | null | undefined): string | null {
    if (!raw) return null;
    if (raw.startsWith('data:') || raw.startsWith('http://') || raw.startsWith('https://')) return raw;
    return `data:image/jpeg;base64,${raw}`;
}

function fieldValue(identity: IdentityPersonItem | null, name: string): string | null {
    const field = identity?.entityFields?.find((f) => f.name === name);
    return field?.stringValue ?? null;
}

/** Pure mapping from REST shapes to a PersonCard. */
export function buildPersonCard(
    edge: EdgePerson,
    identity: IdentityPersonItem | null,
    photoUrl: string | null,
    fields: PersonFieldNames,
): PersonCard {
    const fullName = `${edge.firstName ?? ''} ${edge.lastName ?? ''}`.trim() || 'Unknown';
    return {
        personId: edge.personId ?? '',
        faceId: edge.faceId ?? null,
        fullName,
        vendor: fieldValue(identity, fields.vendor),
        email: fieldValue(identity, fields.email),
        phone: fieldValue(identity, fields.phone),
        dob: fieldValue(identity, fields.dob),
        tags: (edge.tags ?? []).map((t) => t.name ?? '').filter(Boolean),
        enrollmentPhotoUrl: photoUrl,
    };
}

/**
 * Looks up a person via /recognize using a crop-aligned face image (128×128 JPEG,
 * base64-encoded). Returns null when no person matches or on lookup error.
 * NOTE: /recognize requires croppedAligned bytes; expanded is not accepted.
 */
export async function identifyPerson(
    api: Api<unknown>,
    authToken: string,
    croppedAlignedB64: string,
    fields: PersonFieldNames,
): Promise<PersonCard | null> {
    if (!croppedAlignedB64) return null;
    const authHeaders = { headers: { Authorization: `Bearer ${authToken}` } };

    let edge: EdgePerson | null;
    try {
        edge = await api.api.faceEdgeRecognizeLookup(
            {
                croppedAligned: croppedAlignedB64,
                expanded: null,
                embeddings: null,
                recognitionModel: null,
                detectTimeUtc: new Date().toISOString(),
                ianaTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                // Required (server enforces minLength 1) but unused by /recognize itself.
                detectorName: 'eConnect Face Web (lookup)',
            } satisfies FaceEdgeRecognize,
            authHeaders,
        );
    } catch {
        // 404 / lookup error => treat as no person (unknown).
        return null;
    }
    if (!edge || !edge.personId) return null;

    let identity: IdentityPersonItem | null = null;
    let photoUrl: string | null = null;
    try {
        identity = await api.api.getPerson(edge.personId, authHeaders);
    } catch { /* details optional */ }
    if (edge.faceId) {
        // The server returns the enrollment photo as raw image/jpeg bytes; the generated
        // JSON client can't parse that, so fetch it as a blob and use an object URL.
        // (The caller revokes blob: URLs on dismiss.)
        try {
            const resp = await fetch(
                `${api.baseUrl}/api/v1/people/enrollmentPhoto/${encodeURIComponent(edge.faceId)}`,
                { headers: { Authorization: `Bearer ${authToken}` } },
            );
            if (resp.ok) photoUrl = URL.createObjectURL(await resp.blob());
        } catch { /* photo optional */ }
    }

    return buildPersonCard(edge, identity, photoUrl, fields);
}
